/* biome-ignore-all lint/suspicious/noArrayIndexKey: Structured blocks are controlled values without local component state; position is their document identity. */
import { platforms } from "@ondit/publishing";
import { Button, ChipSelect, Input, Label, NativeSelect, Textarea, toast } from "@ondit/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useAccess } from "~/core/access";
import { ApiError, request } from "~/core/api";
import { pick, useI18n } from "~/core/i18n";
import {
	type ContentDocument,
	type ContentItem,
	type ContentKind,
	fileUrl,
	listContent,
	readContent,
	type StudioFile,
	studioCommand,
	studioKey,
} from "./api";
import { issueLabel } from "./issues";
import { VariantEditor } from "./variant";
import "./studio.css";

const names: Record<ContentKind, [string, string]> = {
	text: ["Short text", "短文本"],
	article: ["Article", "文章"],
	gallery: ["Image post", "图文"],
	video: ["Video", "视频"],
};

export function StudioEditor({
	brandId,
	itemId,
	embedded = false,
	inWorkflow = false,
	refreshKey,
	onDirtyChange,
}: {
	brandId: string;
	itemId: string;
	embedded?: boolean;
	inWorkflow?: boolean;
	refreshKey?: string;
	onDirtyChange?: (dirty: boolean) => void;
}) {
	const { locale } = useI18n();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const { editor } = useAccess();
	const cache = useQueryClient();
	const query = useQuery({
		queryKey: [...studioKey(brandId), itemId],
		queryFn: () => readContent(brandId, itemId),
		refetchInterval: (q) =>
			q.state.data?.renders?.some((r) => ["queued", "running"].includes(r.status)) ? 4000 : false,
	});
	useEffect(() => {
		if (refreshKey !== undefined) void cache.invalidateQueries({ queryKey: [...studioKey(brandId), itemId] });
	}, [cache, brandId, itemId, refreshKey]);
	const source = useQuery({
		queryKey: ["content-source", brandId, itemId],
		enabled: !inWorkflow,
		queryFn: () =>
			request<{ workflow_id: string | null; run_id?: string }>(`/agent/content/${brandId}/items/${itemId}/source`),
	});
	const library = useQuery({ queryKey: studioKey(brandId), queryFn: () => listContent(brandId) });
	const [draft, setDraft] = useState<ContentItem | null>(null);
	const [dirty, setDirty] = useState(false);
	const [variantDirty, setVariantDirty] = useState(false);
	const [editSession, setEditSession] = useState(0);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [platform, setPlatform] = useState("");
	const [view, setView] = useState("preview");
	const [device, setDevice] = useState("mobile");
	useEffect(() => {
		if (query.data && !dirty && !variantDirty) setDraft(query.data);
	}, [query.data, dirty, variantDirty]);

	useEffect(() => {
		onDirtyChange?.(dirty || variantDirty);
	}, [dirty, variantDirty, onDirtyChange]);
	const item = draft;
	const changed = !!item && !!query.data && item.revision !== query.data.revision;
	function update(document: ContentDocument) {
		setDraft((d) => (d ? { ...d, document } : d));
		setDirty(true);
	}
	async function action(capability: string, input: unknown) {
		setBusy(true);
		setError("");
		try {
			await studioCommand(brandId, capability, input);
			setDirty(false);
			await cache.invalidateQueries({ queryKey: studioKey(brandId) });
			await cache.invalidateQueries({ queryKey: ["content-flow", brandId] });
			if (capability === "studio.save") {
				setView("preview");
				toast.success(tr("Changes saved", "修改已保存"));
			}
		} catch (e) {
			setError(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}
	if (!item)
		return (
			<div className="studio-workspace">
				{query.error ? <p role="alert">{query.error.message}</p> : tr("Loading…", "加载中…")}
			</div>
		);
	const variant = view === "publish" ? item.variants?.find((v) => v.platform === platform) : undefined;
	const contract = view === "publish" ? platforms.find((p) => p.id === platform) : undefined;
	const files = new Map([...(library.data?.files ?? []), ...(item.files ?? [])].map((f) => [f.id, f]));
	const doc = item.document;
	function attach(id: string) {
		const f = files.get(id);
		if (!f) return;
		const role = f.content_type.startsWith("video/")
			? "video"
			: f.content_type.startsWith("audio/")
				? "audio"
				: f.content_type.startsWith("image/")
					? "image"
					: "subtitle";
		update({ ...doc, media: [...doc.media, { file_id: id, role, alt: f.name }] });
	}
	return (
		<section className="studio-workspace" data-embedded={embedded}>
			<header className="record-heading">
				<div>
					<h2>{item.title}</h2>
					<span className="hint">
						{pick(locale, names[item.kind])} · {tr("Revision", "修订")} {item.revision}
					</span>
				</div>
				{!inWorkflow && source.data?.workflow_id && (
					<Link
						to={`/w/${brandId}/agent/w/${source.data.workflow_id}${source.data.run_id ? `?run=${encodeURIComponent(source.data.run_id)}` : ""}`}
					>
						{tr("Source workflow", "来源流程")}
					</Link>
				)}
				{!inWorkflow && !source.data?.workflow_id && !dirty && !variantDirty && (
					<Link
						to={`/w/${brandId}/agent/home?content=${item.id}&prompt=${encodeURIComponent(tr(`Continue producing this work in a workflow: ${item.title}`, `在流程中继续制作这个作品：${item.title}`))}`}
					>
						{tr("Continue in a workflow", "在流程中继续制作")}
					</Link>
				)}
			</header>
			<div className="studio-toolbar">
				<Button variant={view === "edit" ? "active" : "ghost"} disabled={variantDirty} onClick={() => setView("edit")}>
					{tr("Edit", "编辑")}
				</Button>
				<Button
					variant={view === "preview" ? "active" : "ghost"}
					disabled={variantDirty}
					onClick={() => setView("preview")}
				>
					{tr("Preview", "预览")}
				</Button>
				<Button
					variant={view === "publish" ? "active" : "ghost"}
					disabled={dirty || variantDirty}
					onClick={() => setView("publish")}
				>
					{tr("Review & publish", "审核与发布")}
				</Button>
				{(dirty || variantDirty) && (
					<Button
						disabled={busy}
						onClick={() => {
							setDraft(query.data ?? null);
							setDirty(false);
							setVariantDirty(false);
							setEditSession((n) => n + 1);
						}}
					>
						{tr("Discard changes", "放弃修改")}
					</Button>
				)}
			</div>
			{error && <p role="alert">{error}</p>}
			{changed && (
				<p role="alert">
					{tr(
						"This work changed elsewhere. Reload before saving your edits.",
						"作品已在其他位置更新，请重新载入后再修改。",
					)}
					<Button
						onClick={() => {
							setDirty(false);
							setVariantDirty(false);
							setDraft(query.data ?? null);
						}}
					>
						{tr("Discard edits and reload", "放弃修改并重新载入")}
					</Button>
				</p>
			)}
			{item.renders?.some((r) => ["queued", "running"].includes(r.status)) && (
				<p role="status">
					{tr("Generating media… This result updates automatically.", "正在生成素材，完成后会自动显示。")}
				</p>
			)}
			{item.renders?.[0]?.status === "failed" && (
				<p role="alert">
					{tr(
						"Media generation failed. Your content is saved; review the workflow before trying again.",
						"素材生成失败，内容已保留。请在流程中检查后重试。",
					)}
				</p>
			)}
			{item.renders?.[0]?.output?.attached === false && (
				<p role="status">
					{tr(
						"Files were saved, but this work changed during generation. Add the generated files from Brand files to this work and save, then continue the workflow.",
						"生成文件已保存，但作品在生成期间被修改。请从品牌文件中将生成结果加入作品并保存，再继续流程。",
					)}
				</p>
			)}
			{view === "publish" ? (
				<div className="studio-form">
					<Label>{tr("Choose a platform", "选择发布平台")}</Label>
					<ChipSelect
						fullWidth
						aria-label={tr("Platform", "平台")}
						placeholder={tr("Choose a platform", "选择发布平台")}
						value={platform}
						disabled={variantDirty || busy}
						onChange={setPlatform}
						options={platforms.map((p) => ({ value: p.id, label: p.name }))}
					/>
					{platform && variant && contract ? (
						<VariantEditor
							onDirty={setVariantDirty}
							key={`${variant.id}-${variant.revision}-${editSession}`}
							brandId={brandId}
							item={item}
							variant={variant}
							platform={contract}
							files={files}
						/>
					) : platform ? (
						<>
							<p className="hint">
								{tr(
									"Prepare a version for this platform, then review it before creating a publishing task.",
									"先准备适合该平台的版本，检查后再创建发布任务。",
								)}
							</p>
							<Button
								variant="primary"
								disabled={!editor || busy || dirty}
								onClick={() =>
									void action("studio.adapt", { id: item.id, expected_revision: item.revision, platforms: [platform] })
								}
							>
								{tr("Prepare platform version", "准备平台版本")}
							</Button>
						</>
					) : null}
				</div>
			) : view === "edit" ? (
				<fieldset disabled={!editor || busy} className="studio-form bare-fieldset">
					<Label htmlFor={`title-${itemId}`}>{tr("Title", "标题")}</Label>
					<Input
						id={`title-${itemId}`}
						required
						value={item.title}
						onChange={(e) => {
							setDraft({ ...item, title: e.target.value });
							setDirty(true);
						}}
					/>
					{!item.title.trim() && <p role="alert">{tr("Enter a title before saving.", "请填写标题后再保存。")}</p>}
					<Label htmlFor={`body-${itemId}`}>
						{item.kind === "video" ? tr("Script", "视频脚本") : tr("Body text", "正文")}
					</Label>
					<Textarea
						id={`body-${itemId}`}
						value={doc.body}
						rows={item.kind === "gallery" ? 4 : 6}
						placeholder={
							item.kind === "video"
								? tr(
										"Write the narration or overall script. Add individual scenes below.",
										"填写旁白或整体脚本，在下方添加各个分镜。",
									)
								: tr("Write the content readers will see.", "填写读者将看到的正文内容。")
						}
						onChange={(e) => update({ ...doc, body: e.target.value })}
					/>
					{doc.blocks.map((block, i) => (
						<div className="studio-part" key={`${i}-${block.type}`}>
							<Label htmlFor={`section-${itemId}-${i}`}>
								{block.type === "scene"
									? tr("Scene", "分镜")
									: block.type === "image"
										? tr("Inline image", "正文插图")
										: tr("Paragraph", "段落")}{" "}
								{i + 1}
							</Label>
							{block.type === "image" ? (
								<>
									<Media file={files.get(block.file_id)} alt={block.alt} />
									<Input
										id={`section-${itemId}-${i}`}
										aria-label={tr("Image description", "图片说明")}
										value={block.alt}
										onChange={(e) =>
											update({
												...doc,
												blocks: doc.blocks.map((part, n) => (n === i ? { ...block, alt: e.target.value } : part)),
											})
										}
									/>
								</>
							) : (
								<Textarea
									id={`section-${itemId}-${i}`}
									rows={3}
									value={block.text}
									onChange={(e) =>
										update({
											...doc,
											blocks: doc.blocks.map((b, n) => (n === i ? { ...block, text: e.target.value } : b)),
										})
									}
								/>
							)}
							{block.type === "scene" && (
								<div className="studio-toolbar">
									<Input
										type="number"
										min={1}
										max={120}
										aria-label={tr("Seconds", "秒数")}
										value={block.seconds}
										onChange={(e) =>
											update({
												...doc,
												blocks: doc.blocks.map((b, n) => (n === i ? { ...block, seconds: Number(e.target.value) } : b)),
											})
										}
									/>
									<NativeSelect
										aria-label={tr("Scene image", "分镜图片")}
										value={block.file_id ?? ""}
										onChange={(e) =>
											update({
												...doc,
												blocks: doc.blocks.map((b, n) => (n === i ? { ...block, file_id: e.target.value || null } : b)),
											})
										}
									>
										<option value="">{tr("Text card", "文字卡片")}</option>
										{[...files.values()]
											.filter((f) => f.content_type.startsWith("image/"))
											.map((f) => (
												<option key={f.id} value={f.id}>
													{f.name}
												</option>
											))}
									</NativeSelect>
								</div>
							)}
							<Button variant="ghost" onClick={() => update({ ...doc, blocks: doc.blocks.filter((_, n) => n !== i) })}>
								{tr("Remove", "移除")}
							</Button>
						</div>
					))}
					<div className="studio-toolbar">
						<Button
							onClick={() =>
								update({
									...doc,
									blocks: [
										...doc.blocks,
										item.kind === "video"
											? { type: "scene", text: "", seconds: 5, file_id: null }
											: { type: "text", text: "" },
									],
								})
							}
						>
							{item.kind === "video" ? tr("Add scene", "添加分镜") : tr("Add paragraph", "添加段落")}
						</Button>
						<Link to={`/w/${brandId}/agent/files`}>{tr("Upload in files", "在文件中上传")}</Link>
					</div>
					<Label htmlFor={`assets-${itemId}`}>{tr("Use a brand file", "使用品牌文件")}</Label>
					<ChipSelect
						fullWidth
						aria-label={tr("Use a brand file", "使用品牌文件")}
						value=""
						onChange={attach}
						placeholder={tr("Search brand files…", "搜索品牌文件…")}
						options={(library.data?.files ?? [])
							.filter((file) => /^(image|video|audio)\//.test(file.content_type) || /\.(srt|vtt)$/.test(file.name))
							.map((file) => ({ value: file.id, label: file.name }))}
					/>
					{item.kind === "article" && (
						<NativeSelect
							aria-label={tr("Insert inline image", "插入正文图片")}
							value=""
							onChange={(e) =>
								update({
									...doc,
									blocks: [
										...doc.blocks,
										{ type: "image", file_id: e.target.value, alt: files.get(e.target.value)?.name ?? "" },
									],
								})
							}
						>
							<option value="">{tr("Insert inline image…", "插入正文图片…")}</option>
							{[...files.values()]
								.filter((f) => f.content_type.startsWith("image/"))
								.map((f) => (
									<option key={f.id} value={f.id}>
										{f.name}
									</option>
								))}
						</NativeSelect>
					)}
					<div className="studio-assets">
						{doc.media.map((asset, i) => (
							<figure key={`${asset.file_id}-${i}`}>
								<Media
									file={files.get(asset.file_id)}
									alt={asset.alt}
									caption={doc.media.find((a) => a.role === "subtitle")?.file_id}
								/>
								<Input
									aria-label={tr("Description", "素材说明")}
									value={asset.alt}
									onChange={(e) =>
										update({ ...doc, media: doc.media.map((a, n) => (n === i ? { ...a, alt: e.target.value } : a)) })
									}
								/>
								<div className="studio-toolbar">
									<Button
										disabled={i === 0}
										onClick={() => {
											const media = [...doc.media];
											[media[i - 1], media[i]] = [media[i], media[i - 1]];
											update({ ...doc, media });
										}}
									>
										{tr("Move earlier", "前移")}
									</Button>
									<Button
										variant="ghost"
										onClick={() => update({ ...doc, media: doc.media.filter((_, n) => n !== i) })}
									>
										{tr("Remove", "移除")}
									</Button>
								</div>
							</figure>
						))}
					</div>
					<Label htmlFor={`sources-${itemId}`}>
						{tr("Sources (one HTTPS URL per line)", "来源（每行一个 HTTPS 链接）")}
					</Label>
					<Textarea
						id={`sources-${itemId}`}
						rows={2}
						value={doc.sources.join("\n")}
						onChange={(e) => update({ ...doc, sources: e.target.value.split("\n").filter(Boolean) })}
					/>
					<div className="studio-toolbar">
						<Button
							variant="primary"
							disabled={!dirty || changed || !item.title.trim()}
							onClick={() =>
								void action("studio.save", {
									id: item.id,
									title: item.title,
									kind: item.kind,
									locale: item.locale,
									expected_revision: item.revision,
									document: doc,
								})
							}
						>
							{tr("Save revision", "保存修订")}
						</Button>
					</div>
				</fieldset>
			) : (
				<>
					<div className="studio-toolbar">
						<NativeSelect aria-label={tr("Device", "设备")} value={device} onChange={(e) => setDevice(e.target.value)}>
							<option value="mobile">{tr("Mobile", "移动端")}</option>
							<option value="desktop">{tr("Desktop", "桌面端")}</option>
						</NativeSelect>
						<span className="hint">
							{tr("Layout simulation; platforms may reprocess media.", "版式模拟；平台可能再次处理素材。")}
						</span>
					</div>
					{view === "publish" && platform && !variant ? (
						<p>{tr("Save and prepare this platform version first.", "请先保存并准备此平台版本。")}</p>
					) : (
						<article
							className="studio-preview"
							data-device={device}
							data-layout={contract?.preview.layout ?? item.kind}
						>
							<header>
								<span className="hint">{contract?.name ?? tr("Master", "母版")}</span>
								<h3>{variant?.document.fields.title ?? item.title}</h3>
							</header>

							<div className="studio-preview-media">
								{(variant?.document.media ?? doc.media).map((a, i) => (
									<figure key={`${a.file_id}-${i}`}>
										<Media
											file={files.get(a.file_id)}
											alt={a.alt}
											caption={doc.media.find((a) => a.role === "subtitle")?.file_id}
										/>
										{a.alt && <figcaption>{a.alt}</figcaption>}
									</figure>
								))}
							</div>
							<div className="studio-preview-body">{variant?.document.fields.body ?? doc.body}</div>
							{!variant &&
								doc.blocks.map((b, i) =>
									b.type === "image" ? (
										<figure key={i}>
											<Media file={files.get(b.file_id)} alt={b.alt} />
										</figure>
									) : (
										<p className="studio-preview-body" key={i}>
											{b.text}
										</p>
									),
								)}
						</article>
					)}
					{variant && variant.issues.length > 0 && (
						<div role="status">
							<strong>{tr("Needs attention before delivery", "交付前需要处理")}</strong>
							<ul>
								{variant.issues.map((issue) => (
									<li key={issue}>{issueLabel(issue, locale)}</li>
								))}
							</ul>
						</div>
					)}
				</>
			)}
		</section>
	);
}
function Media({ file, alt, caption }: { file: StudioFile | undefined; alt: string; caption?: string }) {
	if (!file) return <span>{alt}</span>;
	const src = fileUrl(file.id);
	return file.content_type.startsWith("video/") ? (
		// biome-ignore lint/a11y/useMediaCaption: Caption tracks are rendered when the work has an actual subtitle asset.
		<video src={src} controls preload="metadata" aria-label={alt}>
			{caption && <track kind="captions" src={fileUrl(caption)} default />}
		</video>
	) : file.content_type.startsWith("audio/") ? (
		// biome-ignore lint/a11y/useMediaCaption: Uploaded audio may have no subtitle asset; never invent a transcript.
		<audio src={src} controls aria-label={alt}>
			{caption && <track kind="captions" src={fileUrl(caption)} default />}
		</audio>
	) : file.content_type.startsWith("image/") ? (
		<img src={src} alt={alt} />
	) : (
		<a href={src} download>
			{file.name}
		</a>
	);
}

export function ContentSnapshot({
	item,
	expanded = false,
}: {
	item: Pick<ContentItem, "id" | "title" | "revision" | "document" | "files">;
	expanded?: boolean;
}) {
	const { locale } = useI18n();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const files = new Map(item.files.map((file) => [file.id, file]));
	const caption = item.document.media.find((asset) => asset.role === "subtitle")?.file_id;
	const content = (
		<article className="studio-preview studio-result" data-device="desktop">
			<header>
				<h3>{item.title}</h3>
				<span className="hint">
					{tr("Saved output", "本次保存的产物")} · {tr("Version", "版本")} {item.revision}
				</span>
			</header>
			<div className="studio-preview-media">
				{item.document.media.map((asset, index) => (
					<figure key={`${asset.file_id}-${index}`}>
						<Media file={files.get(asset.file_id)} alt={asset.alt} caption={caption} />
						{asset.alt && <figcaption>{asset.alt}</figcaption>}
					</figure>
				))}
			</div>
			{item.document.body && <p className="studio-preview-body">{item.document.body}</p>}
			{item.document.blocks.map((block, index) => (
				<div key={index}>
					{block.type === "scene" && (
						<span className="hint">
							{tr("Scene", "分镜")} {index + 1} · {block.seconds} {tr("seconds", "秒")}
						</span>
					)}
					{block.type !== "text" && block.file_id && (
						<Media
							file={files.get(block.file_id)}
							alt={block.type === "image" ? block.alt : block.text}
							caption={caption}
						/>
					)}
					{block.type !== "image" && <p className="studio-preview-body">{block.text}</p>}
				</div>
			))}
			{!item.document.body && !item.document.blocks.length && !item.document.media.length && (
				<p className="hint">
					{tr(
						"The work is saved, but this step has no text or media to preview yet.",
						"作品已保存，此步骤尚无可预览的正文或素材。",
					)}
				</p>
			)}
			{item.files.length > 0 && (
				<div className="studio-result-files">
					{item.files.map((file) => (
						<a key={file.id} href={fileUrl(file.id)} download>
							{tr("Download", "下载")} · {file.name}
						</a>
					))}
				</div>
			)}
			{item.document.sources.length > 0 && (
				<details>
					<summary>
						{tr("Sources", "内容来源")} · {item.document.sources.length}
					</summary>
					{item.document.sources
						.filter((source) => /^https:\/\//i.test(source))
						.map((source) => (
							<a className="studio-result-source" key={source} href={source} target="_blank" rel="noreferrer">
								{source}
							</a>
						))}
				</details>
			)}
		</article>
	);
	return expanded ? (
		content
	) : (
		<details>
			<summary>
				{tr("Output from this run", "本次执行产物")} · {item.title} · {tr("Version", "版本")} {item.revision}
			</summary>
			{content}
		</details>
	);
}
